import React, { useState, useEffect, useRef } from 'react';
import axios from '../utils/axios';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import ImageLightbox from '../components/ImageLightbox';
import {
  Settings,
  MessageCircle,
  FileText,
  Star,
  Image,
  Save,
  Search,
  X,
  Upload,
  Eye,
  EyeOff,
  User,
  Plus,
  GripVertical,
  Trash2,
} from 'lucide-react';

interface SidebarNote {
  id: string;
  icon: string;
  heading: string;
  description: string;
}

const NOTE_ICONS = [
  { value: 'ℹ️', label: 'Info' },
  { value: '📢', label: 'Announcement' },
  { value: '🔔', label: 'Alert' },
  { value: '⚠️', label: 'Warning' },
  { value: '✅', label: 'Success' },
  { value: '📅', label: 'Event' },
  { value: '⭐', label: 'Star' },
  { value: '🎉', label: 'Celebration' },
  { value: '💡', label: 'Tip' },
  { value: '🕐', label: 'Time' },
  { value: '📌', label: 'Pin' },
  { value: '🚀', label: 'Launch' },
];

interface PortalSettings {
  whatsapp_registration_link: string;
  city_whatsapp_links: string;
  login_sidebar_notes: string;
  volunteer_of_month_id: string;
  volunteer_of_month_name: string;
  volunteer_of_month_photo: string;
  volunteer_of_month_note: string;
  volunteer_of_month_period: string;
  popup_brochure_enabled: string;
  popup_brochure_image_url: string;
  popup_brochure_title: string;
}

interface CityLink {
  city: string;
  link: string;
}

interface MemberOption {
  id: number;
  full_name: string;
  member_id: string;
  profile_image?: string;
}

const SECTION_ICONS: Record<string, React.ReactNode> = {
  whatsapp: <MessageCircle className="w-5 h-5 text-green-500" />,
  citywhatsapp: <MessageCircle className="w-5 h-5 text-green-600" />,
  notes: <FileText className="w-5 h-5 text-blue-500" />,
  volunteer: <Star className="w-5 h-5 text-yellow-500" />,
  brochure: <Image className="w-5 h-5 text-purple-500" />,
};

const AdminSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<PortalSettings>({
    whatsapp_registration_link: '',
    city_whatsapp_links: '',
    login_sidebar_notes: '',
    volunteer_of_month_id: '',
    volunteer_of_month_name: '',
    volunteer_of_month_photo: '',
    volunteer_of_month_note: '',
    volunteer_of_month_period: '',
    popup_brochure_enabled: 'false',
    popup_brochure_image_url: '',
    popup_brochure_title: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [cityLinks, setCityLinks] = useState<CityLink[]>([]);
  const [sidebarNotes, setSidebarNotes] = useState<SidebarNote[]>([]);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberResults, setMemberResults] = useState<MemberOption[]>([]);
  const [memberSearching, setMemberSearching] = useState(false);
  const [showBrochurePreview, setShowBrochurePreview] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get('/settings');
      const raw = res.data.settings || {};
      setSettings((prev) => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(raw).map(([k, v]) => [k, v === null ? '' : String(v)])
        ),
      }));
      // Parse sidebar notes JSON
      try {
        const parsed = JSON.parse(raw.login_sidebar_notes || '[]');
        setSidebarNotes(Array.isArray(parsed) ? parsed : []);
      } catch {
        setSidebarNotes([]);
      }
      // Parse city whatsapp links JSON
      try {
        const cityMap = JSON.parse(raw.city_whatsapp_links || '{}');
        if (cityMap && typeof cityMap === 'object') {
          setCityLinks(Object.entries(cityMap).map(([city, link]) => ({ city, link: String(link) })));
        }
      } catch {
        setCityLinks([]);
      }
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (keys: (keyof PortalSettings)[], label: string) => {
    setSaving(label);
    try {
      const payload: Partial<PortalSettings> = {};
      keys.forEach((k) => {
        payload[k] = settings[k];
      });
      await axios.put('/settings', payload);
      toast.success(`${label} saved successfully`);
    } catch {
      toast.error(`Failed to save ${label}`);
    } finally {
      setSaving(null);
    }
  };

  const handleChange = (key: keyof PortalSettings, value: string) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const searchMembers = async (query: string) => {
    if (!query.trim()) {
      setMemberResults([]);
      return;
    }
    setMemberSearching(true);
    try {
      const res = await axios.get('/members', {
        params: { search: query, limit: 8, page: 1, status: 'approved' },
      });
      const list: MemberOption[] = (res.data?.data || []).map((m: any) => ({
        id: m.id,
        full_name: m.full_name,
        member_id: m.member_id,
        profile_image: m.profile_image,
      }));
      setMemberResults(list);
    } catch {
      setMemberResults([]);
    } finally {
      setMemberSearching(false);
    }
  };

  const handleMemberSearchChange = (val: string) => {
    setMemberSearch(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchMembers(val), 350);
  };

  const selectMember = (member: MemberOption) => {
    setSettings((prev) => ({
      ...prev,
      volunteer_of_month_id: String(member.id),
      volunteer_of_month_name: member.full_name,
      volunteer_of_month_photo: member.profile_image || '',
    }));
    setMemberSearch('');
    setMemberResults([]);
  };

  const clearVolunteer = () => {
    setSettings((prev) => ({
      ...prev,
      volunteer_of_month_id: '',
      volunteer_of_month_name: '',
      volunteer_of_month_photo: '',
    }));
  };

  // ── Sidebar notes helpers ──
  const addNote = () => {
    setSidebarNotes((prev) => [
      ...prev,
      { id: Date.now().toString(), icon: 'ℹ️', heading: '', description: '' },
    ]);
  };

  const removeNote = (id: string) => {
    setSidebarNotes((prev) => prev.filter((n) => n.id !== id));
  };

  const updateNote = (id: string, field: keyof SidebarNote, value: string) => {
    setSidebarNotes((prev) =>
      prev.map((n) => (n.id === id ? { ...n, [field]: value } : n))
    );
  };

  const saveNotes = async () => {
    setSaving('Notes');
    try {
      await axios.put('/settings', {
        login_sidebar_notes: JSON.stringify(sidebarNotes),
      });
      toast.success('Notes saved successfully');
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSaving(null);
    }
  };

  // ── City WhatsApp links helpers ──
  const addCityLink = () => setCityLinks((prev) => [...prev, { city: '', link: '' }]);

  const removeCityLink = (idx: number) => setCityLinks((prev) => prev.filter((_, i) => i !== idx));

  const updateCityLink = (idx: number, field: keyof CityLink, value: string) => {
    setCityLinks((prev) => prev.map((cl, i) => (i === idx ? { ...cl, [field]: value } : cl)));
  };

  const saveCityLinks = async () => {
    const invalid = cityLinks.some((cl) => !cl.city.trim() || !cl.link.trim());
    if (invalid) {
      toast.error('Please fill in both city name and WhatsApp link for each row');
      return;
    }
    setSaving('CityWhatsApp');
    try {
      const cityMap = Object.fromEntries(cityLinks.map((cl) => [cl.city.trim(), cl.link.trim()]));
      await axios.put('/settings', { city_whatsapp_links: JSON.stringify(cityMap) });
      toast.success('City WhatsApp links saved');
    } catch {
      toast.error('Failed to save city WhatsApp links');
    } finally {
      setSaving(null);
    }
  };

  const handleBrochureImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      handleChange('popup_brochure_image_url', reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0D5128]" />
        </div>
      </div>
    );
  }

  const SaveButton: React.FC<{ section: string; label: string; onClick: () => void }> = ({
    section,
    label,
    onClick,
  }) => (
    <button
      onClick={onClick}
      disabled={saving === label}
      className="flex items-center gap-2 bg-[#0D5128] hover:bg-green-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-60"
    >
      {saving === label ? (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
      ) : (
        <Save className="w-4 h-4" />
      )}
      Save {section}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-[#0D5128] p-2 rounded-xl">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#0D5128]">Portal Settings</h1>
          </div>
          <p className="text-gray-500 text-sm ml-14">
            Configure login page content, volunteer of the month, and portal-wide settings.
          </p>
        </div>

        <div className="space-y-6">
          {/* ── WhatsApp Registration Link ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-5">
              {SECTION_ICONS.whatsapp}
              <h2 className="text-lg font-semibold text-gray-800">WhatsApp Registration Link</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              This link will appear on the login page sidebar so new volunteers can join your WhatsApp group.
            </p>
            <div className="flex gap-3">
              <input
                type="url"
                value={settings.whatsapp_registration_link}
                onChange={(e) => handleChange('whatsapp_registration_link', e.target.value)}
                placeholder="https://chat.whatsapp.com/..."
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5128]"
              />
              <SaveButton
                section=""
                label="WhatsApp"
                onClick={() => saveSection(['whatsapp_registration_link'], 'WhatsApp')}
              />
            </div>
          </div>

          {/* ── City WhatsApp Groups ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-3">
                {SECTION_ICONS.citywhatsapp}
                <h2 className="text-lg font-semibold text-gray-800">City WhatsApp Groups</h2>
              </div>
              <button
                onClick={addCityLink}
                className="flex items-center gap-1.5 bg-[#0D5128] hover:bg-green-800 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
              >
                <Plus className="w-4 h-4" />
                Add City
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5 ml-8">
              Add a WhatsApp group invite link for each city. When a member registers with that city, this link will be included in their verification email.
            </p>

            {cityLinks.length === 0 && (
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center mb-4">
                <MessageCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No cities added yet. Click <strong>Add City</strong> to start.</p>
              </div>
            )}

            <div className="space-y-3 mb-5">
              {cityLinks.map((cl, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <input
                    type="text"
                    value={cl.city}
                    onChange={(e) => updateCityLink(idx, 'city', e.target.value)}
                    placeholder="City name (e.g. Karachi)"
                    className="w-36 flex-shrink-0 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5128]"
                  />
                  <input
                    type="url"
                    value={cl.link}
                    onChange={(e) => updateCityLink(idx, 'link', e.target.value)}
                    placeholder="https://chat.whatsapp.com/..."
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5128]"
                  />
                  <button
                    onClick={() => removeCityLink(idx)}
                    className="p-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    title="Remove"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {cityLinks.length > 0 && (
              <SaveButton section="" label="CityWhatsApp" onClick={saveCityLinks} />
            )}
          </div>

          {/* ── Login Sidebar Notes ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-3">
                {SECTION_ICONS.notes}
                <h2 className="text-lg font-semibold text-gray-800">Login Sidebar Notes</h2>
              </div>
              <button
                onClick={addNote}
                className="flex items-center gap-1.5 bg-[#0D5128] hover:bg-green-800 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-all"
              >
                <Plus className="w-4 h-4" />
                Add Note
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-5 ml-8">
              Add multiple notes with an icon, heading, and description. They appear stacked on the login page sidebar.
            </p>

            {sidebarNotes.length === 0 && (
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center mb-4">
                <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No notes yet. Click <strong>Add Note</strong> to create one.</p>
              </div>
            )}

            <div className="space-y-4 mb-5">
              {sidebarNotes.map((note, idx) => (
                <div
                  key={note.id}
                  className="border border-gray-200 rounded-xl p-4 bg-gray-50 relative"
                >
                  {/* Note number badge */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-gray-300" />
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                        Note {idx + 1}
                      </span>
                    </div>
                    <button
                      onClick={() => removeNote(note.id)}
                      className="p-1 text-gray-400 hover:text-red-500 transition-colors rounded"
                      title="Remove note"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="grid grid-cols-[auto_1fr] gap-3 mb-3">
                    {/* Icon picker */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Icon</label>
                      <select
                        value={note.icon}
                        onChange={(e) => updateNote(note.id, 'icon', e.target.value)}
                        className="border border-gray-300 rounded-lg px-2 py-2 text-lg bg-white focus:outline-none focus:ring-2 focus:ring-[#0D5128] cursor-pointer"
                        style={{ minWidth: '70px' }}
                      >
                        {NOTE_ICONS.map((ic) => (
                          <option key={ic.value} value={ic.value}>
                            {ic.value} {ic.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Heading */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1.5">Heading</label>
                      <input
                        type="text"
                        value={note.heading}
                        onChange={(e) => updateNote(note.id, 'heading', e.target.value)}
                        placeholder="e.g. Registration Open"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D5128]"
                      />
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1.5">Description</label>
                    <textarea
                      value={note.description}
                      onChange={(e) => updateNote(note.id, 'description', e.target.value)}
                      rows={2}
                      placeholder="e.g. Join us on Saturday at 10am for the community drive..."
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0D5128] resize-none"
                    />
                  </div>

                  {/* Live preview */}
                  {(note.heading || note.description) && (
                    <div className="mt-3 flex items-start gap-3 bg-[#C49A3C]/15 rounded-lg p-3 border border-[#C49A3C]/30">
                      <span className="text-lg shrink-0">{note.icon}</span>
                      <div>
                        {note.heading && <p className="text-sm font-semibold text-[#0D5128]">{note.heading}</p>}
                        {note.description && <p className="text-xs text-gray-600 mt-0.5">{note.description}</p>}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              {sidebarNotes.length > 0 && (
                <p className="text-xs text-gray-400">{sidebarNotes.length} note{sidebarNotes.length !== 1 ? 's' : ''} will show on login page</p>
              )}
              <div className="ml-auto">
                <SaveButton section="" label="Notes" onClick={saveNotes} />
              </div>
            </div>
          </div>

          {/* ── Volunteer of the Month ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-5">
              {SECTION_ICONS.volunteer}
              <h2 className="text-lg font-semibold text-gray-800">Volunteer of the Month</h2>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              Highlight a top volunteer on the login sidebar. Search by name or member ID to select them.
            </p>

            {/* Selected volunteer preview */}
            {settings.volunteer_of_month_name && (
              <div className="flex items-center gap-4 bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-5">
                {settings.volunteer_of_month_photo ? (
                  <img
                    src={settings.volunteer_of_month_photo}
                    alt={settings.volunteer_of_month_name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-yellow-300"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-yellow-200 flex items-center justify-center">
                    <User className="w-7 h-7 text-yellow-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800">{settings.volunteer_of_month_name}</p>
                  {settings.volunteer_of_month_period && (
                    <p className="text-xs text-yellow-700">{settings.volunteer_of_month_period}</p>
                  )}
                </div>
                <button
                  onClick={clearVolunteer}
                  className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                  title="Clear selection"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Member search */}
            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="w-4 h-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={memberSearch}
                onChange={(e) => handleMemberSearchChange(e.target.value)}
                placeholder="Search member by name or ID..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5128]"
              />
              {memberSearching && (
                <div className="absolute inset-y-0 right-3 flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0D5128]" />
                </div>
              )}
            </div>

            {memberResults.length > 0 && (
              <div className="border border-gray-200 rounded-xl overflow-hidden mb-4 shadow-sm">
                {memberResults.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => selectMember(m)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 text-left transition-colors"
                  >
                    {m.profile_image ? (
                      <img
                        src={m.profile_image}
                        alt={m.full_name}
                        onClick={(e) => { e.stopPropagation(); setLightboxSrc(m.profile_image!); }}
                        className="w-9 h-9 rounded-full object-cover cursor-zoom-in"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#0D5128]/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-[#0D5128]" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-800">{m.full_name}</p>
                      <p className="text-xs text-gray-500">ID: {m.member_id}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                  Display Name (override)
                </label>
                <input
                  type="text"
                  value={settings.volunteer_of_month_name}
                  onChange={(e) => handleChange('volunteer_of_month_name', e.target.value)}
                  placeholder="Full name"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5128]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Period</label>
                <input
                  type="text"
                  value={settings.volunteer_of_month_period}
                  onChange={(e) => handleChange('volunteer_of_month_period', e.target.value)}
                  placeholder="e.g. June 2026"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5128]"
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                Achievement Note
              </label>
              <input
                type="text"
                value={settings.volunteer_of_month_note}
                onChange={(e) => handleChange('volunteer_of_month_note', e.target.value)}
                placeholder="e.g. Attended 12 events this month with outstanding dedication"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5128]"
              />
            </div>

            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                Photo URL (auto-filled from member profile)
              </label>
              <input
                type="text"
                value={settings.volunteer_of_month_photo}
                onChange={(e) => handleChange('volunteer_of_month_photo', e.target.value)}
                placeholder="https://... or leave blank for avatar"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5128]"
              />
            </div>

            <div className="flex justify-end">
              <SaveButton
                section=""
                label="Volunteer"
                onClick={() =>
                  saveSection(
                    [
                      'volunteer_of_month_id',
                      'volunteer_of_month_name',
                      'volunteer_of_month_photo',
                      'volunteer_of_month_note',
                      'volunteer_of_month_period',
                    ],
                    'Volunteer'
                  )
                }
              />
            </div>
          </div>

          {/* ── Popup Brochure ── */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-5">
              {SECTION_ICONS.brochure}
              <h2 className="text-lg font-semibold text-gray-800">Login Page Popup Brochure</h2>
            </div>
            <p className="text-sm text-gray-500 mb-5">
              Upload a brochure or poster image. It will display as a popup when visitors open the login page.
            </p>

            {/* Toggle */}
            <div className="flex items-center gap-3 mb-5">
              <button
                onClick={() =>
                  handleChange(
                    'popup_brochure_enabled',
                    settings.popup_brochure_enabled === 'true' ? 'false' : 'true'
                  )
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.popup_brochure_enabled === 'true' ? 'bg-[#0D5128]' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.popup_brochure_enabled === 'true' ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-700">
                {settings.popup_brochure_enabled === 'true' ? 'Popup is enabled' : 'Popup is disabled'}
              </span>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Popup Title (optional)</label>
              <input
                type="text"
                value={settings.popup_brochure_title}
                onChange={(e) => handleChange('popup_brochure_title', e.target.value)}
                placeholder="e.g. Hammad Foundation — July Drive 2026"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D5128]"
              />
            </div>

            {/* Image upload */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                Brochure Image
              </label>
              <label className="flex items-center gap-3 border-2 border-dashed border-gray-300 rounded-xl p-4 cursor-pointer hover:border-[#0D5128] transition-colors">
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-500">Click to upload an image (PNG, JPG, WebP)</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleBrochureImageUpload}
                  className="hidden"
                />
              </label>
            </div>

            {settings.popup_brochure_image_url && (
              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-600">Current Image</span>
                  <button
                    onClick={() => setShowBrochurePreview((p) => !p)}
                    className="flex items-center gap-1 text-xs text-[#0D5128] hover:underline"
                  >
                    {showBrochurePreview ? (
                      <><EyeOff className="w-3.5 h-3.5" /> Hide Preview</>
                    ) : (
                      <><Eye className="w-3.5 h-3.5" /> Show Preview</>
                    )}
                  </button>
                </div>
                {showBrochurePreview && (
                  <img
                    src={settings.popup_brochure_image_url}
                    alt="Brochure preview"
                    className="max-h-80 rounded-xl object-contain border border-gray-200 w-full"
                  />
                )}
                <button
                  onClick={() => handleChange('popup_brochure_image_url', '')}
                  className="mt-2 flex items-center gap-1.5 text-xs text-red-500 hover:text-red-700"
                >
                  <X className="w-3.5 h-3.5" /> Remove image
                </button>
              </div>
            )}

            <div className="flex justify-end">
              <SaveButton
                section=""
                label="Brochure"
                onClick={() =>
                  saveSection(
                    ['popup_brochure_enabled', 'popup_brochure_image_url', 'popup_brochure_title'],
                    'Brochure'
                  )
                }
              />
            </div>
          </div>
        </div>
      </div>
      <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
    </div>
  );
};

export default AdminSettingsPage;
